import { DRAFT_PATH, INTEGRATIONS_PATH } from '@constants/documents.constants';

import { FlowType } from '@domain/types';
import electronService from '@services/electron/electron.service';
import { logger } from '@services/logger/logger.service';
import { history } from '@store/history';
import { NOTIFICATION_LOADED_MESSAGE, NOTIFICATION_SHOW_MAIN } from './constants';
import { RELAY_CHANNELS } from './relayChannels';

class NotificationWindow {
  private win: any | undefined;

  public get isInitialized() {
    return !!this.win;
  }

  public async init() {
    /*
    Notification window is always running while the app is running.
    We only show or hide it based on events or user interaction.
    This is so it does not go through the app loader every time
    we want to display something inside of it.
   */
    if (this.win) {
      return;
    }

    this.win = await electronService.createNotificationWindow(
      `${window.location.origin}/apps/notification`,
      {
        /* We send an initial message to the notification window so it can get the main window's ID
        WARNING: This will only work with bridge API v1.2.1+, or the polyfill */
        onDidFinishLoad: () => {
          this.sendMessageToNotificationWindow(NOTIFICATION_LOADED_MESSAGE);
        },
      },
    );

    /* Destroy notification window if main app is about to close */
    window.addEventListener('beforeunload', async () => {
      await this.win?.destroy();
    });

    const ipcRenderer = window.electron.getIpcRenderer();

    /*
     * Show & focus main window when notification window asks for it,
     * and redirect to correct path depending on flow Type.
     */
    const flowTypesPathMap: Record<FlowType, { path: string }> = {
      sending: { path: DRAFT_PATH },
      integration: { path: INTEGRATIONS_PATH },
    };

    Object.entries(flowTypesPathMap).forEach(([flowType, { path }]) => {
      ipcRenderer.on(`${NOTIFICATION_SHOW_MAIN}/${flowType}`, () => {
        /* Renderer-triggered: this raises, focuses and navigates the main window, so leave a
           trace of every request to make an abusive burst visible in the logs. */
        logger.info('DESKTOP', 'Notification window asked to show the main window', { flowType });
        window.electron.showCurrentWindow();
        window.electron.focusCurrentWindow();
        window.electron.restoreCurrentWindow();
        history.push(path);
      });
    });

    /* Wait for notification window to send us a message indicating that is has finished loading */
    await new Promise<void>((resolve) => {
      window.electron.getIpcRenderer().once(NOTIFICATION_LOADED_MESSAGE, () => {
        resolve();
      });
    });
  }

  public async showNotifWindow() {
    if (await this.win?.isVisible?.()) return; // isVisible is not exposed before 100.14.3
    /*
      We normally prevent the window from being destroyed.
      However, we rebuild the window as a safety measure
      if that's the case. Then we wait a little bit
      for the app to boot.
    */
    if (!this.win || (await this.win.isDestroyed())) {
      this.win = undefined;
      await this.init();
    }
    await this.win.show();
  }

  public sendMessageToNotificationWindow = (channel: string, data?: any) => {
    /* The main process rejects anything outside its own allowlist; failing here as well turns
       a silently swallowed IPC rejection (see the .catch in bridge/util.api.ts) into a log. */
    if (!RELAY_CHANNELS.has(channel)) {
      logger.error('DESKTOP', 'Refused to relay an unexpected channel to the notification window', {
        channel,
      });
      return;
    }

    const webContentsId = this.win?.getWebContentId?.() || this.win?.webContentsId;

    if (webContentsId) {
      window.electron.getIpcRenderer().sendTo(webContentsId, channel, data);
    }
  };
}

export const notificationWindow = new NotificationWindow();
